import React, { useEffect, useState, useRef } from "react";
import ProductCard from "../../component/common/productCard";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { FCApiService } from "../../service/fCApiService";
import { ProductsApiService } from "../../../service/website/productsApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { createMarkup } from "../../helper/utils";
import Pagination from "../../../component/library/pagination";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import { SubscriptionApiService } from "../../../service/website/subscriptionApiService";
import { AuthApiService } from "../../../service/authApiService";
import { ProductSubscriptionApiService } from "../../../service/seller/productSubscriptionApiService";
import WebButton from "../../../component/library/webButton";
import {
  getStoredSelectedProducts,
  storeSelectedProducts,
  clearStoredSelectedProducts,
  addProductToStorage,
  removeProductFromStorage,
  isProductStoredAsSelected,
} from '../../helper/selectedProductsStorage';
import StickySelectionBar from '../../component/common/StickySelectionBar';
import SubscriptionModal from '../../component/common/SubscriptionModal';
import "./style.css";
import "../products/style.css";

// Use env-based total products per page (same as products page)
const TOTAL_PRODUCTS = parseInt(process.env.REACT_APP_TOTAL_PRODUCTS || '12', 10);

export default function FeaturedCategory() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const message = useMessage();
  const navigate = useNavigate();

  const isAllProductsPage = params.category === "all-products";
  const [isSubscribingAll, setIsSubscribingAll] = useState(false);
  const [subscribingSectionIndex, setSubscribingSectionIndex] = useState(null);

  // Track subscription status for each section
  const [subscriptionStatus, setSubscriptionStatus] = useState({});

  // Multi-product selection state (for Subscribe All button)
  const [selectedProductsByKeyword, setSelectedProductsByKeyword] = useState({});
  const [currentKeyword, setCurrentKeyword] = useState('all-products');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const [data, setData] = useState([]);
  const [renderedData, setRenderedData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // For list blocks (offerIds pagination), reorder ids so "Send Inquiry" products
  // (non-pricable / isPricable=false) appear only on later pages.
  const [orderedOfferIdsByIdx, setOrderedOfferIdsByIdx] = useState({});

  const [allProducts, setAllProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Per-section page for featured category (e.g. Daily Specials): 12 products per page
  const [sectionPages, setSectionPages] = useState({});
  // Products loaded per section per page (key: `${sectionIdx}_${page}`) – load 12 from server per page
  const [sectionProductsCache, setSectionProductsCache] = useState({});
  const [sectionLoading, setSectionLoading] = useState({});
  const sectionFetchInFlightRef = useRef(new Set());
  const [currentUserId, setCurrentUserId] = useState(null);
  const [updateTrigger, setUpdateTrigger] = useState(0); // Force re-render after subscription update

  // Amazon Friendly and Subscribed Products filter states
  const [showAmazonFriendly, setShowAmazonFriendly] = useState(false);
  const [showSubscribedProducts, setShowSubscribedProducts] = useState(false);
  const [originalProducts, setOriginalProducts] = useState([]); // Store original products for filter restoration

  /* ================= GET CURRENT USER ID ================= */
  useEffect(() => {
    const auth = getAuthLocalStorage();
    if (auth.isLoggedIn && auth.loginHeader) {
      // Get user ID from profile API
      AuthApiService.getProfile()
        .then(res => {
          if (res?.status?.code === 200 && res?.data?._id) {
            setCurrentUserId(res.data._id);
          }
        })
        .catch(() => {
          // If profile fetch fails, we'll rely on subscribe field
        });
    }
  }, []);

  /* ================= ALL PRODUCTS ================= */
  useEffect(() => {
    if (!isAllProductsPage) return;

    const fetchAllProducts = async () => {
      setIsLoading(true);

      // Reset filters when fetching new products
      setShowAmazonFriendly(false);
      setShowSubscribedProducts(false);
      setOriginalProducts([]);

      const res = await ProductsApiService.getProducts(
        `?page=${page}&limit=${TOTAL_PRODUCTS}&allProducts=true`
      );

      if (res?.status?.code === 200) {
        setAllProducts(res.data.products || []);
        setTotalPages(res.data.totalPages || 1);
      }
      setIsLoading(false);
    };

    fetchAllProducts();
  }, [isAllProductsPage, page]);

  /* ================= FEATURED CATEGORY ================= */
  useEffect(() => {
    if (isAllProductsPage) return;

    let isMounted = true;
    setIsLoading(true);
    setRenderedData([]);

    const id = searchParams.get("id");

    const loadCategory = async () => {
      try {
        if (id) {
          const res = await FCApiService.getCategoryById(id);
          if (isMounted && res?.status?.code === 200) {
            setData(res.data || []);
          }
          return;
        }

        if (params.category) {
          // ⚡ Step 1: Lightweight fetch to find matching category without heavy product data
          const res = await FCApiService.getCategories(true);
          const list = res.data || [];
          let match = list.find(c =>
            (c.pageUrl || "").replace(/^\//, "").includes(params.category)
          );

          if (match) {
            // ⚡ Step 2: Fetch FULL details for ONLY the matched category
            const fullRes = await FCApiService.getCategoryById(match._id);
            if (fullRes?.status?.code === 200) {
              match = fullRes.data;
            }

            const hasListBlocksWithData = match?.content?.some(
              (b) => (b.type === "list" || b.type === "carousel") && b.data && String(b.data).trim().length > 0
            );

            if (match?.content && hasListBlocksWithData) {
              console.log("[FeaturedCategory] Loaded by content blocks (manual SKUs):", match.tagName, "blocks:", match.content?.length);
              setData(match.content);
              return;
            }
          }

          // Fallback: use tag-based products when no manual SKU blocks
          const homeRes = await ProductsApiService.getHomeProducts();
          const homeList = homeRes?.data || [];
          const matchBySlug = homeList.find(c => {
            const url = (c.pageUrl || "").replace(/^\//, "").replace(/^fc\/?/, "");
            return url.includes(params.category) || (c.name && c.name.toLowerCase().replace(/\s+/g, "-").includes(params.category));
          });

          if (matchBySlug && matchBySlug.products && matchBySlug.products.length > 0) {
            console.log("[FeaturedCategory] Loaded by tag (fallback):", matchBySlug.name, "products count:", matchBySlug.products.length);
            setData([{ type: "list", products: matchBySlug.products, heading: matchBySlug.name || "Products", data: "" }]);
            return;
          }

          if (match?.content) {
            setData(match.content);
          } else {
            setData([]);
            setRenderedData([]);
            setIsLoading(false);
          }
        }
      } catch (err) {
        console.error("[FeaturedCategory] loadCategory error:", err);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        setIsLoading(false);
      }
    };

    loadCategory();
    return () => (isMounted = false);
  }, [params.category, searchParams.get("id"), isAllProductsPage]);

  /* ================= TRANSFORM FC CONTENT ================= */
  /* Store list blocks with offerIds only; products are loaded 12 per page on demand */
  useEffect(() => {
    if (isAllProductsPage || !data.length) return;

    const parsed = data.map(item => {
      if (item.type !== "list") return item;
      if (item.products?.length) return item; // e.g. tag-based fallback already has products
      if (!item.data) {
        console.warn("[FeaturedCategory] List block has empty Product SKUs – add comma-separated SKUs or offerIds in admin.");
        return { ...item, products: [], offerIds: [] };
      }

      const rawIds = item.data.split(/[,\n]/).map(x => x.trim()).filter(Boolean);
      const invalidKeywords = new Set(["items", "sku", "offer id", "offerid", "product", "product name", "heading"]);
      const ids = rawIds.filter((id) => {
        const lower = id.toLowerCase().trim();
        if (invalidKeywords.has(lower)) return false;
        if (id.length < 3) return false;
        return true;
      });
      if (rawIds.length !== ids.length) {
        console.warn("[FeaturedCategory] Filtered out invalid IDs:", rawIds.filter((x) => !ids.includes(x)));
      }
      console.log("[FeaturedCategory] List block ids:", ids.length, "– products will load 12 per page");
      return { ...item, offerIds: ids, totalCount: ids.length };
    });

    const hasListWithIds = parsed.some(p => p.type === "list" && (p.offerIds?.length > 0 || p.products?.length > 0));
    setRenderedData(parsed);
    setOrderedOfferIdsByIdx({});
    setSectionProductsCache({});
    setSectionPages({});
    setSectionLoading({});
    setIsLoading(false);

    // If no list blocks with data, try tag-based fallback
    if (!hasListWithIds && params.category) {
      ProductsApiService.getHomeProducts().then(homeRes => {
        const homeList = homeRes?.data || [];
        const match = homeList.find(c => {
          const url = (c.pageUrl || "").replace(/^\//, "").replace(/^fc\/?/, "");
          return url.includes(params.category) || (c.name && c.name.toLowerCase().replace(/\s+/g, "-").includes(params.category));
        });
        if (match?.products?.length > 0) {
          console.log("[FeaturedCategory] Fallback: showing", match.products.length, "products from category tag");
          setRenderedData([{ type: "list", products: match.products, heading: match.name || "Products", data: "" }]);
        }
      });
    }
  }, [data, isAllProductsPage, params.category]);

  // ================= ORDER OFFER IDS (non-inquiry first) =================
  // FeaturedCategory paginates by slicing `offerIds` (12 per page).
  // To push "Send Inquiry" products to later pages, we reorder the full offerIds list once.
  useEffect(() => {
    if (isAllProductsPage || !renderedData.length) return;

    let cancelled = false;

    const chunkArray = (arr, size) => {
      const out = [];
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
      return out;
    };

    const orderOfferIds = async (offerIds) => {
      const ids = (offerIds || []).map(x => String(x).trim()).filter(Boolean);
      if (!ids.length) return [];

      // Fetch products in chunks so we can classify by isPricable.
      const CHUNK = 60;
      const chunks = chunkArray(ids, CHUNK);
      const productResults = await Promise.all(
        chunks.map(c =>
          ProductsApiService.getProductsByOfferIds({ offerIds: c }).catch(() => null)
        )
      );

      const products = productResults.flatMap(r => r?.data || []);

      // Map id -> isPricable
      const idToPricable = new Map();
      for (const p of products) {
        if (p?.offerId != null) idToPricable.set(String(p.offerId).trim(), p?.isPricable === true);
        if (p?.sku != null) idToPricable.set(String(p.sku).trim(), p?.isPricable === true);
      }

      // Stable ordering: non-inquiry first, then inquiry.
      const pricableIds = [];
      const inquiryIds = [];
      for (const id of ids) {
        const key = String(id).trim();
        if (idToPricable.get(key) === true) pricableIds.push(id);
        else inquiryIds.push(id);
      }
      return [...pricableIds, ...inquiryIds];
    };

    const run = async () => {
      const ordering = {};
      for (let idx = 0; idx < renderedData.length; idx++) {
        const item = renderedData[idx];
        if (item?.type !== "list" || !item?.offerIds?.length) continue;
        ordering[idx] = await orderOfferIds(item.offerIds);
      }

      if (!cancelled) setOrderedOfferIdsByIdx(ordering);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [renderedData, isAllProductsPage]);

  /* ================= FETCH 12 PRODUCTS PER PAGE FOR LIST SECTIONS (offerIds) ================= */
  useEffect(() => {
    if (isAllProductsPage || !renderedData.length) return;

    renderedData.forEach((item, idx) => {
      if (item.type !== "list" || !item.offerIds?.length) return;
      const currentPage = sectionPages[idx] ?? 1;
      const key = `${idx}_${currentPage}`;
      if (sectionProductsCache[key] !== undefined) return;
      if (sectionFetchInFlightRef.current.has(key)) return;

      // Wait until we've reordered offerIds for this list block, otherwise we'd
      // fetch the wrong 12 ids (and inquiry items would appear early).
      if (!Object.prototype.hasOwnProperty.call(orderedOfferIdsByIdx, idx)) return;
      const sourceOfferIds = orderedOfferIdsByIdx[idx] || item.offerIds;

      const start = (currentPage - 1) * TOTAL_PRODUCTS;
      const pageOfferIds = sourceOfferIds.slice(start, start + TOTAL_PRODUCTS);
      if (pageOfferIds.length === 0) return;

      sectionFetchInFlightRef.current.add(key);
      setSectionLoading(prev => ({ ...prev, [idx]: true }));

      ProductsApiService.getProductsByOfferIds({ offerIds: pageOfferIds })
        .then(res => {
          const products = res?.data || [];
          setSectionProductsCache(prev => ({ ...prev, [key]: products }));
        })
        .catch(err => {
          console.error("[FeaturedCategory] Error loading page products:", err);
          setSectionProductsCache(prev => ({ ...prev, [key]: [] }));
        })
        .finally(() => {
          sectionFetchInFlightRef.current.delete(key);
          setSectionLoading(prev => ({ ...prev, [idx]: false }));
        });
    });
  }, [renderedData, sectionPages, isAllProductsPage, orderedOfferIdsByIdx]);

  /* ================= CHECK IF PRODUCTS CAN BE SUBSCRIBED ================= */
  const getSubscribableProducts = (products) => {
    if (!products || products.length === 0) return [];
    // Products need SKU and must be dropshipping (moq === 1) to be subscribable
    return products.filter(p => p.sku && p.moq === 1);
  };

  /* ================= CHECK SUBSCRIPTION STATUS ================= */
  const checkSubscriptionStatus = (products) => {
    if (!products || products.length === 0) return { allSubscribed: false, allUnsubscribed: false, count: 0, subscribableCount: 0 };

    const subscribableProducts = getSubscribableProducts(products);

    if (subscribableProducts.length === 0) {
      return { allSubscribed: false, allUnsubscribed: false, count: 0, subscribableCount: 0 };
    }

    const auth = getAuthLocalStorage();

    // Check if products are subscribed
    const subscribedCount = subscribableProducts.filter(p => {
      // Primary check: subscribe boolean field (most reliable)
      if (p.subscribe === true) return true;

      // Secondary check: userSubscribe array - check if current user is subscribed
      if (p.userSubscribe && Array.isArray(p.userSubscribe) && p.userSubscribe.length > 0) {
        // If we have current user ID, check if it's in the array
        if (currentUserId) {
          return p.userSubscribe.some(id => {
            const idStr = typeof id === 'string' ? id : (id._id || id.toString());
            return idStr === currentUserId || idStr === String(currentUserId);
          });
        }
        // If no user ID but array has items, for featured category pages assume subscribed
        // For "all products" page, we need user ID to verify
        if (!isAllProductsPage) {
          return true;
        }
      }

      return false;
    }).length;

    const totalCount = subscribableProducts.length;

    return {
      allSubscribed: subscribedCount === totalCount && totalCount > 0,
      allUnsubscribed: subscribedCount === 0,
      count: subscribedCount,
      total: totalCount,
      subscribableCount: totalCount
    };
  };

  /* ================= HANDLE AMAZON FRIENDLY FILTER ================= */
  const handleAmazonFriendlyClick = () => {
    if (showAmazonFriendly) {
      // Switch back to all products - restore from original
      setShowAmazonFriendly(false);
      setAllProducts(originalProducts.length > 0 ? originalProducts : allProducts);
      message.showToast('Showing all products', ToastTypes.SUCCESS);
    } else {
      // Store current products before filtering
      if (originalProducts.length === 0 && allProducts.length > 0) {
        setOriginalProducts(allProducts);
      }

      // Filter products that have valid images
      const amazonFriendlyProducts = allProducts.filter(product => {
        const img = product.image || product.imgUrl?.[0];
        if (!img) return false;
        const imgStr = String(img).trim();
        return imgStr !== '';
      });

      setAllProducts(amazonFriendlyProducts);
      setShowAmazonFriendly(true);
      message.showToast(`Showing ${amazonFriendlyProducts.length} Amazon Friendly product(s)`, ToastTypes.SUCCESS);
    }
  };

  /* ================= HANDLE SUBSCRIBED PRODUCTS FILTER ================= */
  const handleSubscribeProductClick = async () => {
    const auth = getAuthLocalStorage();
    if (!auth.isLoggedIn || !auth.loginHeader) {
      message.showToast("Please login to view subscribed products", ToastTypes.WARNING);
      navigate("/login");
      return;
    }

    if (showSubscribedProducts) {
      // Switch back to all products - restore from original
      setShowSubscribedProducts(false);
      setAllProducts(originalProducts.length > 0 ? originalProducts : allProducts);
      message.showToast('Showing all products', ToastTypes.SUCCESS);
    } else {
      try {
        setIsLoading(true);

        // Store current products before filtering
        if (originalProducts.length === 0 && allProducts.length > 0) {
          setOriginalProducts(allProducts);
        }

        // Fetch subscribed products
        const response = await ProductSubscriptionApiService.getProductSubscription('?limit=10000');

        if (response && response.status && response.status.code === 200 && response.data && response.data.products) {
          let subscribedProducts = response.data.products;

          if (subscribedProducts.length === 0) {
            message.showToast('No subscribed products found', ToastTypes.INFO);
            setIsLoading(false);
            return;
          }

          // Format products to ensure imgUrl is an array and set subscribe flag
          subscribedProducts = subscribedProducts.map(product => ({
            ...product,
            subscribe: true, // Mark as subscribed so heart icon shows as blue/filled
            imgUrl: Array.isArray(product.imgUrl) ? product.imgUrl : (product.imgUrl ? [product.imgUrl] : []),
            sp: Number(product.sp || 0),
            price: Number(product.price || 0),
            moq: Number(product.moq || 0),
          }));

          setAllProducts(subscribedProducts);
          setShowSubscribedProducts(true);
          message.showToast(`Showing ${subscribedProducts.length} subscribed product(s)`, ToastTypes.SUCCESS);
        } else {
          message.showToast('Failed to fetch subscribed products', ToastTypes.ERROR);
        }
      } catch (error) {
        console.error('Error fetching subscribed products:', error);
        message.showToast('Error fetching subscribed products', ToastTypes.ERROR);
      } finally {
        setIsLoading(false);
      }
    }
  };

  /* ================= CHECK IF USER IS AUTHENTICATED SELLER ================= */
  const isAuthenticatedSeller = () => {
    const auth = getAuthLocalStorage();
    return auth.isLoggedIn && auth.loginHeader && (auth.userType === 'seller' || auth.userType === 'admin' || auth.userType === 'sub-admin');
  };

  /* ================= CHECK IF PRODUCT IS SELECTED ================= */
  const isProductSelected = (product) => {
    const keyword = currentKeyword || 'all-products';
    const selectedProducts = selectedProductsByKeyword[keyword] || [];
    return selectedProducts.some(p => (p.sku || p.offerId) === (product.sku || product.offerId));
  };

  /* ================= GET SELECTABLE PRODUCTS COUNT ================= */
  const getSelectableProductsCount = () => {
    return allProducts.filter(product => product?.moq === 1).length;
  };

  /* ================= GET SELECTED PRODUCTS ON PAGE COUNT ================= */
  const getSelectedProductsOnPageCount = () => {
    const selectableProducts = allProducts.filter(product => product?.moq === 1);
    return selectableProducts.filter(product => isProductSelected(product)).length;
  };

  /* ================= HANDLE PRODUCT SELECTION ================= */
  const handleProductSelection = (product, isSelected) => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      message.showToast('Please login as a seller to select products', ToastTypes.WARNING);
      return;
    }

    const keyword = currentKeyword || 'all-products';

    setSelectedProductsByKeyword(prev => {
      const newState = { ...prev };

      if (!newState[keyword]) {
        newState[keyword] = [];
      }

      if (isSelected) {
        // Add product if not already selected for this keyword
        const isAlreadySelected = newState[keyword].some(p => (p.sku || p.offerId) === (product.sku || product.offerId));
        if (!isAlreadySelected) {
          newState[keyword] = [...newState[keyword], { ...product, keyword }];
          // Also update localStorage immediately
          addProductToStorage(keyword, product);
        }
      } else {
        // Remove product from this keyword group
        const productId = product.sku || product.offerId;
        newState[keyword] = newState[keyword].filter(p => (p.sku || p.offerId) !== productId);

        // Remove from storage
        removeProductFromStorage(keyword, productId);

        // Clean up empty keyword groups
        if (newState[keyword].length === 0) {
          delete newState[keyword];
        }
      }

      return newState;
    });
  };

  /* ================= HANDLE SELECT ALL (from products page) ================= */
  const handleSelectAll = () => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      message.showToast('Please login as a seller to select products', ToastTypes.WARNING);
      return;
    }

    const selectableProducts = allProducts.filter(product => product?.moq === 1);

    if (selectableProducts.length === 0) {
      message.showToast('No selectable products found on this page', ToastTypes.INFO);
      return;
    }

    const keyword = currentKeyword || 'all-products';

    setSelectedProductsByKeyword(prev => {
      const newState = { ...prev };

      if (!newState[keyword]) {
        newState[keyword] = [];
      }

      // Add all selectable products that aren't already selected
      selectableProducts.forEach(product => {
        const isAlreadySelected = newState[keyword].some(p => (p.sku || p.offerId) === (product.sku || product.offerId));
        if (!isAlreadySelected) {
          newState[keyword] = [...newState[keyword], { ...product, keyword }];
          addProductToStorage(keyword, product);
        }
      });

      return newState;
    });

    message.showToast(`Selected ${selectableProducts.length} products`, ToastTypes.SUCCESS);
  };

  /* ================= LOAD PERSISTED SELECTED PRODUCTS ================= */
  useEffect(() => {
    if (isAllProductsPage) {
      const storedProducts = getStoredSelectedProducts();
      if (Object.keys(storedProducts).length > 0) {
        setSelectedProductsByKeyword(storedProducts);
      }
    }
  }, [isAllProductsPage]);

  /* ================= PERSIST SELECTED PRODUCTS ================= */
  useEffect(() => {
    if (isAllProductsPage && Object.keys(selectedProductsByKeyword).length > 0) {
      storeSelectedProducts(selectedProductsByKeyword);
    } else if (isAllProductsPage) {
      clearStoredSelectedProducts();
    }
  }, [selectedProductsByKeyword, isAllProductsPage]);

  /* ================= GET TOTAL SELECTED COUNT ================= */
  const getTotalSelectedCount = () => {
    return Object.values(selectedProductsByKeyword).reduce((total, products) => total + products.length, 0);
  };

  /* ================= HANDLE PRODUCT REMOVE FROM MODAL ================= */
  const handleProductRemoveFromModal = (keyword, productId, updatedState) => {
    setSelectedProductsByKeyword(updatedState);
  };

  /* ================= CLEAR ALL SELECTIONS ================= */
  const handleClearSelections = () => {
    setSelectedProductsByKeyword({});
    clearStoredSelectedProducts();
    message.showToast('All selections cleared', ToastTypes.SUCCESS);
  };

  /* ================= OPEN SUBSCRIPTION MODAL ================= */
  const handleOpenSubscriptionModal = () => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      message.showToast('Please login as a seller to subscribe', ToastTypes.WARNING);
      return;
    }
    setShowSubscriptionModal(true);
  };

  /* ================= HANDLE MULTIPLE SUBSCRIPTION ================= */
  const handleMultipleSubscription = async subscriptionData => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      message.showToast('Please login as a seller to subscribe', ToastTypes.WARNING);
      return;
    }

    setSubscriptionLoading(true);
    try {
      const { products } = subscriptionData;

      if (!products || products.length === 0) {
        message.showToast('No products to subscribe', ToastTypes.WARNING);
        return;
      }

      // For featured category all products page, these are MongoDB products
      const mongoProducts = [];
      const alibabaProducts = [];

      products.forEach(product => {
        // Check if product has offerId (Alibaba product) or _id (MongoDB product)
        const hasOfferId = product.offerId;
        const hasMongoId = product._id && /^[0-9a-fA-F]{24}$/.test(String(product._id));

        if (hasOfferId && !hasMongoId) {
          alibabaProducts.push(product);
        } else {
          mongoProducts.push(product);
        }
      });

      console.log('Subscription breakdown:', {
        mongoProducts: mongoProducts.length,
        alibabaProducts: alibabaProducts.length,
        total: products.length
      });

      // Subscribe MongoDB products (SKU-based)
      if (mongoProducts.length > 0) {
        const mongoSkus = mongoProducts.map(p => p.sku).filter(Boolean);
        if (mongoSkus.length > 0) {
          try {
            const mongoRes = await ProductSubscriptionApiService.multipleProductSubscribe({
              skus: mongoSkus
            });
            if (mongoRes?.status?.code === 200) {
              console.log(`✅ Subscribed ${mongoSkus.length} MongoDB products`);
            }
          } catch (err) {
            console.error('Error subscribing MongoDB products:', err);
          }
        }
      }

      // Subscribe Alibaba products (OfferId-based)
      if (alibabaProducts.length > 0) {
        const alibabaOfferIds = alibabaProducts.map(p => p.offerId).filter(Boolean);
        if (alibabaOfferIds.length > 0) {
          try {
            const alibabaRes = await ProductSubscriptionApiService.subscribeToMultipleAlibabaProducts({
              offerIds: alibabaOfferIds
            });
            if (alibabaRes?.status?.code === 200) {
              console.log(`✅ Subscribed ${alibabaOfferIds.length} Alibaba products`);
            }
          } catch (err) {
            console.error('Error subscribing Alibaba products:', err);
          }
        }
      }

      const totalSubscribed = mongoProducts.length + alibabaProducts.length;
      if (totalSubscribed > 0) {
        message.showToast(`Successfully subscribed to ${totalSubscribed} product(s)`, ToastTypes.SUCCESS);
        setSelectedProductsByKeyword({});
        clearStoredSelectedProducts();
        setShowSubscriptionModal(false);

        // Refresh products to update subscription status
        if (isAllProductsPage) {
          const res = await ProductsApiService.getProducts(
            `?page=${page}&limit=50&allProducts=true`
          );
          if (res?.status?.code === 200) {
            setAllProducts(res.data.products || []);
          }
        }
      } else {
        message.showToast('No products were subscribed', ToastTypes.WARNING);
      }
    } catch (error) {
      console.error('Error in multiple subscription:', error);
      message.showToast(error?.message || 'Failed to subscribe products', ToastTypes.ERROR);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  /* ================= SUBSCRIBE/UNSUBSCRIBE ALL FUNCTION (for featured category sections) ================= */
  const handleSubscribeAll = async (products, sectionIndex = null) => {
    // Check if user is logged in
    const auth = getAuthLocalStorage();
    if (!auth.isLoggedIn || !auth.loginHeader) {
      message.showToast("Please login to subscribe to products", ToastTypes.WARNING);
      navigate("/login");
      return;
    }

    if (!products || products.length === 0) {
      message.showToast("No products available", ToastTypes.WARNING);
      return;
    }

    const status = checkSubscriptionStatus(products);
    const shouldUnsubscribe = status.allSubscribed;

    setIsSubscribingAll(true);
    if (sectionIndex !== null) {
      setSubscribingSectionIndex(sectionIndex);
    }

    let successCount = 0;
    let errorCount = 0;

    try {
      // Filter only products with SKU (subscribable products)
      const subscribableProducts = products.filter(p => p.sku);

      if (subscribableProducts.length === 0) {
        message.showToast("No subscribable products available", ToastTypes.WARNING);
        setIsSubscribingAll(false);
        setSubscribingSectionIndex(null);
        return;
      }

      // Subscribe or unsubscribe to all subscribable products
      const actionPromises = subscribableProducts.map(async (product) => {
        try {
          let res;
          if (shouldUnsubscribe) {
            res = await SubscriptionApiService.unSubscribeProduct(product.sku);
          } else {
            res = await SubscriptionApiService.subscribeProduct(product.sku);
          }

          if (res.status.code === 200) {
            return { success: true };
          } else {
            return { success: false };
          }
        } catch (error) {
          console.error(`Error ${shouldUnsubscribe ? 'unsubscribing from' : 'subscribing to'} product ${product.sku}:`, error);
          return { success: false };
        }
      });

      const results = await Promise.all(actionPromises);
      successCount = results.filter(r => r.success).length;
      errorCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        const action = shouldUnsubscribe ? 'unsubscribed from' : 'subscribed to';
        // Only show successful subscriptions, don't show failed count
        message.showToast(
          `Successfully ${action} ${successCount} product${successCount > 1 ? 's' : ''}`,
          ToastTypes.SUCCESS
        );

        // Update product subscription status in real-time without page refresh
        if (isAllProductsPage) {
          // Update allProducts state
          setAllProducts(prevProducts => {
            const updated = prevProducts.map(product => {
              if (subscribableProducts.some(p => p.sku === product.sku)) {
                return {
                  ...product,
                  subscribe: !shouldUnsubscribe,
                  userSubscribe: shouldUnsubscribe
                    ? (product.userSubscribe || []).filter(id => {
                      const idStr = typeof id === 'string' ? id : (id._id || id.toString());
                      return idStr !== currentUserId && idStr !== String(currentUserId);
                    })
                    : (() => {
                      const userSubs = product.userSubscribe || [];
                      const userExists = userSubs.some(id => {
                        const idStr = typeof id === 'string' ? id : (id._id || id.toString());
                        return idStr === currentUserId || idStr === String(currentUserId);
                      });
                      return userExists ? userSubs : [...userSubs, currentUserId];
                    })()
                };
              }
              return product;
            });
            // Trigger re-render
            setUpdateTrigger(prev => prev + 1);
            return updated;
          });
        } else if (sectionIndex !== null) {
          // Update renderedData state for featured category sections
          setRenderedData(prevData => {
            const updated = prevData.map((item, idx) => {
              if (idx === sectionIndex && item.type === 'list' && item.products) {
                return {
                  ...item,
                  products: item.products.map(product => {
                    if (subscribableProducts.some(p => p.sku === product.sku)) {
                      return {
                        ...product,
                        subscribe: !shouldUnsubscribe,
                        userSubscribe: shouldUnsubscribe
                          ? (product.userSubscribe || []).filter(id => {
                            const idStr = typeof id === 'string' ? id : (id._id || id.toString());
                            return idStr !== currentUserId && idStr !== String(currentUserId);
                          })
                          : (() => {
                            const userSubs = product.userSubscribe || [];
                            const userExists = userSubs.some(id => {
                              const idStr = typeof id === 'string' ? id : (id._id || id.toString());
                              return idStr === currentUserId || idStr === String(currentUserId);
                            });
                            return userExists ? userSubs : [...userSubs, currentUserId];
                          })()
                      };
                    }
                    return product;
                  })
                };
              }
              return item;
            });
            // Trigger re-render
            setUpdateTrigger(prev => prev + 1);
            return updated;
          });
        }
      } else {
        message.showToast(`Failed to ${shouldUnsubscribe ? 'unsubscribe from' : 'subscribe to'} products. Please try again.`, ToastTypes.ERROR);
      }
    } catch (error) {
      console.error(`Error ${shouldUnsubscribe ? 'unsubscribing from' : 'subscribing to'} all products:`, error);
      message.showToast("An error occurred. Please try again.", ToastTypes.ERROR);
    } finally {
      setIsSubscribingAll(false);
      setSubscribingSectionIndex(null);
    }
  };

  // Skeleton count for initial loading (match grid: 4 columns, 2–3 rows)
  const SKELETON_COUNT = 8;

  return (
    <>
      <div className="fc-page-wrapper">
        {/* ================= ALL PRODUCTS ================= */}
        {isAllProductsPage && (
          <>
            <div className="fc_product_listing_head_row">
              <h1>All Products</h1>
            </div>

            {/* Filter and Subscribe Buttons */}
            <div className="sorting-and-filters">
              <div className="filter-buttons-left">
                <button
                  className={`filter-btn filter-btn-secondary ${showAmazonFriendly ? 'active' : ''}`}
                  onClick={handleAmazonFriendlyClick}
                  title={showAmazonFriendly ? 'Show All Products' : 'Amazon Friendly'}
                >
                  {showAmazonFriendly ? 'Show All Products' : 'Amazon Friendly'}
                </button>
                <button
                  className={`filter-btn filter-btn-secondary ${showSubscribedProducts ? 'active' : ''}`}
                  onClick={handleSubscribeProductClick}
                  title={showSubscribedProducts ? 'Show All Products' : 'Subscribe Product'}
                >
                  {showSubscribedProducts ? 'Show All Products' : 'Subscribe Product'}
                </button>
                {isAuthenticatedSeller() && getSelectableProductsCount() > 0 && (
                  <button
                    className={`filter-btn filter-btn-secondary ${getSelectedProductsOnPageCount() === getSelectableProductsCount() ? 'active' : ''}`}
                    onClick={handleSelectAll}
                    disabled={getSelectedProductsOnPageCount() === getSelectableProductsCount()}
                    title="Subscribe to all products on this page"
                  >
                    Subscribe All
                  </button>
                )}
              </div>
              <div className="sorting-container-right">
                {/* Subscribe all button moved to filter-buttons-left section below */}
              </div>
            </div>

            <div className="fc_product_listing">
              {isLoading ? (
                Array.from({ length: TOTAL_PRODUCTS }).map((_, idx) => (
                  <div key={`skeleton-${idx}`} className="product_card_box skeleton_card">
                    <div className="skeleton_img" />
                    <div className="skeleton_text skeleton_text-lg" />
                    <div className="skeleton_text skeleton_text-sm" />
                    <div className="skeleton_text skeleton_text-sm" style={{ width: '40%', marginTop: '8px' }} />
                  </div>
                ))
              ) : (
                allProducts.map((product, index) => {
                  const isSelectable = isAuthenticatedSeller();
                  const isSelected = isProductSelected(product);
                  return (
                    <ProductCard
                      key={product._id || product.sku || index}
                      product={product}
                      index={index}
                      isSelectable={isSelectable}
                      isSelected={isSelected}
                      onSelectionChange={handleProductSelection}
                      keyword={currentKeyword}
                      isFromFeaturedCategory={true}
                    />
                  );
                })
              )}
            </div>

            {!isLoading && totalPages > 1 && (
              <div className="collection_pagination">
                <Pagination
                  pages={{ totalPages }}
                  page={page}
                  setPage={setPage}
                />
              </div>
            )}
          </>
        )}

        {/* ================= FEATURED CATEGORY (e.g. Daily Specials) ================= */}
        {!isAllProductsPage && isLoading && (
          <>
            <div className="fc_product_listing_head_row">
              <h1 className="fc_skeleton_heading">Loading...</h1>
            </div>
            <div className="fc_product_listing">
              {Array.from({ length: SKELETON_COUNT }).map((_, idx) => (
                <div key={`skeleton-${idx}`} className="product_card_box skeleton_card">
                  <div className="skeleton_img" />
                  <div className="skeleton_text skeleton_text-lg" />
                  <div className="skeleton_text skeleton_text-sm" />
                  <div className="skeleton_text skeleton_text-sm" style={{ width: '40%', marginTop: '8px' }} />
                </div>
              ))}
            </div>
          </>
        )}
        {!isAllProductsPage && !isLoading &&
          renderedData.map((item, idx) => {
            if (item.type === "html") {
              if (item.visible === false) return null;
              return (
                <div
                  key={idx}
                  className="featured_category_section"
                  dangerouslySetInnerHTML={createMarkup(item.data)}
                />
              );
            }

            if (item.type === "list") {
              const hasOfferIds = item.offerIds?.length > 0;
              const allProducts = hasOfferIds ? null : (item.products || []);
              const currentSectionPage = sectionPages[idx] ?? 1;
              const cacheKey = `${idx}_${currentSectionPage}`;
              const orderedAllProducts = !hasOfferIds
                ? [...(allProducts || []).filter(p => p?.isPricable === true), ...(allProducts || []).filter(p => p?.isPricable !== true)]
                : null;
              const paginatedProducts = hasOfferIds
                ? (sectionProductsCache[cacheKey] || [])
                : (orderedAllProducts.slice((currentSectionPage - 1) * TOTAL_PRODUCTS, currentSectionPage * TOTAL_PRODUCTS));
              const totalCount = hasOfferIds ? (item.totalCount || 0) : allProducts.length;
              const sectionTotalPages = Math.max(1, Math.ceil(totalCount / TOTAL_PRODUCTS));
              const isLoadingSection = hasOfferIds && (sectionLoading[idx] || sectionProductsCache[cacheKey] === undefined);

              return (
                <div key={idx}>
                  <div className="fc_product_listing_head_row">
                    <h1>{item.heading}</h1>
                  </div>

                  {(() => {
                    const productsForSubscribe = hasOfferIds
                      ? Object.keys(sectionProductsCache)
                        .filter(k => k.startsWith(`${idx}_`))
                        .flatMap(k => sectionProductsCache[k] || [])
                      : allProducts;
                    const subscribableProducts = getSubscribableProducts(productsForSubscribe);
                    if (subscribableProducts.length === 0 || subscribableProducts.length === 1) return null;

                    const status = checkSubscriptionStatus(subscribableProducts);
                    const isProcessing = isSubscribingAll && subscribingSectionIndex === idx;
                    const buttonText = isProcessing
                      ? (status.allSubscribed ? "Unsubscribing..." : "Subscribing...")
                      : (status.allSubscribed ? "Unsubscribe all" : "Subscribe all");

                    return (
                      <div className="subscribe_all_button_container">
                        <WebButton
                          className={`subscribe_all_btn ${status.allSubscribed ? 'unsubscribe_btn' : ''}`}
                          text={buttonText}
                          onClick={() => handleSubscribeAll(subscribableProducts, idx)}
                          disabled={isSubscribingAll}
                        />
                      </div>
                    );
                  })()}

                  <div className="fc_product_listing">
                    {isLoadingSection ? (
                      Array.from({ length: TOTAL_PRODUCTS }).map((_, i) => (
                        <div key={`skeleton-${i}`} className="product_card_box skeleton_card">
                          <div className="skeleton_img" />
                          <div className="skeleton_text skeleton_text-lg" />
                          <div className="skeleton_text skeleton_text-sm" />
                          <div className="skeleton_text skeleton_text-sm" style={{ width: '40%', marginTop: '8px' }} />
                        </div>
                      ))
                    ) : (
                      paginatedProducts.map((p, i) => (
                        <ProductCard
                          key={p._id || p.sku || i}
                          product={p}
                          index={i}
                          isSelectable
                          isFromFeaturedCategory={true}
                        />
                      ))
                    )}
                  </div>

                  {sectionTotalPages > 1 && !isLoadingSection && (
                    <div className="collection_pagination">
                      <Pagination
                        pages={{ totalPages: sectionTotalPages }}
                        page={currentSectionPage}
                        setPage={(newPage) => setSectionPages(prev => ({ ...prev, [idx]: newPage }))}
                      />
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
      </div>

      {/* Multi-product subscription components for all products page */}
      {isAllProductsPage && getTotalSelectedCount() > 0 && (
        <StickySelectionBar
          selectedProductsByKeyword={selectedProductsByKeyword}
          onSubscribe={handleOpenSubscriptionModal}
          onClear={handleClearSelections}
          isLoading={subscriptionLoading}
        />
      )}

      {/* Subscription Modal */}
      {isAllProductsPage && (
        <SubscriptionModal
          show={showSubscriptionModal}
          onHide={() => setShowSubscriptionModal(false)}
          selectedProductsByKeyword={selectedProductsByKeyword}
          onSubscribe={handleMultipleSubscription}
          onProductRemove={handleProductRemoveFromModal}
          isLoading={subscriptionLoading}
        />
      )}
    </>
  );
}
